import {
  type AlphaIbkrAccountDetailField,
  type AlphaIbkrAccountType,
  type SavedAlphaIbkrAccountDetails,
} from "./alphaIbkrAccountDetails";
import type { InvestorAlphaIbkrSetupResponse } from "../../types/investorAlpha";
import { ALPHA_COUNTRIES } from "./alphaCountries";

const STORAGE_PREFIX = "plutus_alpha_ibkr_account_details_draft";
const COUNTRY_CODES = new Set(ALPHA_COUNTRIES.map((country) => country.code));

const storageKey = (userId: string): string => `${STORAGE_PREFIX}:${userId}`;

const isAccountType = (value: unknown): value is AlphaIbkrAccountType => (
  value === "taxable" || value === "retirement"
);

export interface AlphaIbkrAccountDetailsDraft extends SavedAlphaIbkrAccountDetails {
  setupResponse?: InvestorAlphaIbkrSetupResponse;
}

export type AlphaIbkrAccountDetailsDraftField =
  | AlphaIbkrAccountDetailField
  | "setupResponse";

export function loadAlphaIbkrAccountDetailsDraft(
  userId: string | null
): AlphaIbkrAccountDetailsDraft {
  if (!userId) return {};

  try {
    const raw = window.localStorage.getItem(storageKey(userId));
    if (!raw) return {};
    const parsed = JSON.parse(raw) as Record<string, unknown>;
    if (!parsed || typeof parsed !== "object" || Array.isArray(parsed)) return {};

    const draft: AlphaIbkrAccountDetailsDraft = {};
    if (typeof parsed.accountName === "string") draft.accountName = parsed.accountName;
    if (
      typeof parsed.countryCode === "string"
      && COUNTRY_CODES.has(parsed.countryCode)
    ) draft.countryCode = parsed.countryCode;
    if (isAccountType(parsed.accountType)) draft.accountType = parsed.accountType;
    if (parsed.setupResponse === "yes-existing" || parsed.setupResponse === "yes-new") {
      draft.setupResponse = parsed.setupResponse;
    }
    return draft;
  } catch {
    return {};
  }
}

export function updateAlphaIbkrAccountDetailsDraft(
  userId: string | null,
  field: AlphaIbkrAccountDetailsDraftField,
  value: string
): void {
  if (!userId) return;

  try {
    const draft = loadAlphaIbkrAccountDetailsDraft(userId);
    window.localStorage.setItem(storageKey(userId), JSON.stringify({
      ...draft,
      [field]: value,
    }));
  } catch {
    // The in-memory answer remains usable when storage is unavailable.
  }
}

export function removeAlphaIbkrAccountDetailsDraftField(
  userId: string | null,
  field: AlphaIbkrAccountDetailsDraftField,
  savedValue: string
): void {
  if (!userId) return;

  try {
    const draft = loadAlphaIbkrAccountDetailsDraft(userId);
    const currentValue = draft[field];
    const matchesSavedValue = field === "accountName" && typeof currentValue === "string"
      ? currentValue.trim() === savedValue
      : currentValue === savedValue;
    if (!matchesSavedValue) return;
    delete draft[field];
    if (Object.keys(draft).length === 0) {
      window.localStorage.removeItem(storageKey(userId));
    } else {
      window.localStorage.setItem(storageKey(userId), JSON.stringify(draft));
    }
  } catch {
    // A later save can retry cleanup.
  }
}

export function clearAlphaIbkrAccountDetailsDraft(userId: string | null): void {
  if (!userId) return;
  try {
    window.localStorage.removeItem(storageKey(userId));
  } catch {
    // Storage cleanup is best effort.
  }
}
